import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import API from "@/lib/api";
import { toast } from "sonner";
import { Gift, Share2, Copy } from "lucide-react";
import useLoyaltyRules from "@/hooks/useLoyaltyRules";

/**
 * One-tap "Refer a friend, both get $20" banner inside the Companion App.
 * Lives high on the home screen — daily-engagement surface drives organic
 * referral lift. Reuses existing /api/referrals/mine plumbing.
 *
 * Behavior:
 *  - Tap "Share" → native Web Share sheet (iOS/Android Messages, Mail,
 *    AirDrop, etc.) with pre-filled message + signup URL
 *  - Falls back to clipboard copy on browsers without navigator.share
 *  - Tap the banner body → /referrals (full hub)
 *  - Hidden when /referrals/mine fails or returns no code (defensive)
 */
export default function CompanionReferralBanner() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const rules = useLoyaltyRules();
  const bonusPoints = Number(rules.companion_install_bonus_points || 0);

  useEffect(() => {
    API.get("/referrals/mine")
      .then(({ data }) => setData(data))
      .catch(() => setData(null));
  }, []);

  if (!data?.referral_code) return null;

  const shareUrl = `${window.location.origin}/register?ref=${data.referral_code}`;
  const bonusLine = bonusPoints > 0
    ? ` I'll also earn ${bonusPoints.toLocaleString()} bonus reward points once you place your first medication order.`
    : "";
  const message = `Join me on GLPFlo for medically-supervised GLP-1 medications. Use my code ${data.referral_code} at signup and we both get a $${data.reward_amount} off coupon!${bonusLine} ${shareUrl}`;

  const share = async (e) => {
    e.stopPropagation();
    if (navigator.share) {
      try {
        await navigator.share({ title: "Join me on GLPFlo", text: message, url: shareUrl });
      } catch (_) { /* user cancelled */ }
    } else {
      try {
        await navigator.clipboard.writeText(message);
        toast.success("Share message copied — paste it anywhere");
      } catch {
        toast.error("Copy failed");
      }
    }
  };

  const copyCode = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(data.referral_code);
      toast.success(`Copied ${data.referral_code}`);
    } catch {
      toast.error("Copy failed");
    }
  };

  const reward = data.reward_amount || 20;

  return (
    <button
      type="button"
      onClick={() => navigate("/referrals")}
      className="w-full text-left rounded-2xl bg-gradient-to-br from-amber-400 via-amber-300 to-orange-300 shadow-md hover:shadow-lg active:scale-[0.99] transition-all p-4 relative overflow-hidden"
      data-testid="companion-referral-banner"
    >
      {/* Decorative gift icon */}
      <Gift className="absolute -right-3 -bottom-3 w-24 h-24 text-amber-100/40 pointer-events-none" />

      <div className="relative flex items-start gap-3">
        <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-white/40 backdrop-blur-sm flex items-center justify-center">
          <Gift className="w-5 h-5 text-amber-900" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-wider font-bold text-amber-900/80">
            Refer a friend
          </p>
          <h3 className="text-sm font-bold text-amber-950 leading-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Both get a ${reward} off coupon — one tap
          </h3>
          {bonusPoints > 0 && (
            <p className="text-[11px] font-bold text-purple-800 mt-0.5" data-testid="companion-referral-bonus-line">
              +{bonusPoints.toLocaleString()} bonus points on signup 🎁
            </p>
          )}
          <button
            type="button"
            onClick={copyCode}
            className="mt-1.5 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-white/60 hover:bg-white/80 active:bg-white text-amber-950 font-mono text-[11px] font-bold transition-colors"
            data-testid="companion-referral-code-copy"
          >
            {data.referral_code}
            <Copy className="w-3 h-3" />
          </button>
        </div>
        <button
          type="button"
          onClick={share}
          className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-amber-950 text-amber-50 font-bold text-xs hover:bg-amber-900 active:scale-95 transition-all shadow-md"
          data-testid="companion-referral-share-btn"
        >
          <Share2 className="w-3.5 h-3.5" />
          Share
        </button>
      </div>
    </button>
  );
}
